/**
 * useSpeechSynthesis — thin hook over the browser's SpeechSynthesis API.
 * Zero server cost, ships with Chrome / Safari / Edge, supports every
 * Sancta language (auto-picks the best matching voice on the platform).
 *
 * Only ONE utterance can be active at a time across all consumers of the
 * hook — starting a new `speak()` cancels any previous one. The
 * currently-speaking message is tracked via `currentId` so a chat widget
 * can tag exactly which reply's speaker button is active.
 */
import { useEffect, useRef, useState } from "react";

/**
 * BCP-47 code map for the 12 Sancta languages. Uses regional codes that
 * major TTS engines actually ship voices for.
 */
const LANG_MAP: Record<string, string> = {
  en: "en-US",
  hi: "hi-IN",
  ta: "ta-IN",
  te: "te-IN",
  kn: "kn-IN",
  ml: "ml-IN",
  bn: "bn-IN",
  mr: "mr-IN",
  gu: "gu-IN",
  es: "es-ES",
  ar: "ar-SA",
  fr: "fr-FR",
};

interface SpeechState {
  supported: boolean;
  speaking: boolean;
  currentId: string | null;
  speak: (id: string, text: string, lang?: string) => void;
  stop: () => void;
}

// Sancta always speaks with a warm, locale-matched FEMALE voice — never male.
// Browsers don't expose gender, so we score by known female / male voice names
// shipped across Google, Microsoft (Azure) and Apple TTS engines.
const FEMALE_HINTS = [
  "female", "woman", "aria", "jenny", "zira", "hazel", "susan", "samantha",
  "karen", "moira", "tessa", "fiona", "veena", "heera", "kalpana", "swara",
  "neerja", "sneha", "kavya", "pallavi", "sara", "amelie", "julie", "celine",
  "monica", "paulina", "helena", "isabela", "joana", "salma", "zariyah",
  "amina", "google", "lekha", "vani", "shruti", "pooja", "ananya", "priya",
]; // "google" voices are typically neutral/female-leaning per locale
const MALE_HINTS = [
  "male", "man", "rishi", "prabhat", "hemant", "madhur", "ravi", "rajesh",
  "daniel", "alex", "fred", "george", "thomas", "diego", "jorge", "carlos",
  "hoda", "naayf", "pablo", "guy", "eric", "brian", "mark", "david",
];

function _isFemaleName(name: string): number {
  const n = name.toLowerCase();
  if (MALE_HINTS.some(h => n.includes(h))) return -100;
  if (FEMALE_HINTS.some(h => n.includes(h))) return 100;
  return 0;
}

// Strip emojis, markdown syntax, inline Sancta block tags and URLs so the
// voice engine never reads symbols or emoji names aloud. Text stays visible
// on screen untouched — this only affects what is spoken.
function cleanForSpeech(input: string): string {
  if (!input) return "";
  let s = input;
  // Drop Sancta inline block tags together with their JSON payloads.
  s = s.replace(/<(products|compare|dupe|sibascore|cart|login)>[\s\S]*?<\/\1>/g, " ");
  s = s.replace(/<\/?(products|compare|dupe|sibascore|cart|login)>/g, " ");
  // Links [text](url) -> text ; strip bare URLs entirely.
  s = s.replace(/\[([^\]]+)\]\((?:https?:\/\/[^\s)]+)\)/g, "$1");
  s = s.replace(/https?:\/\/[^\s]+/g, " ");
  // Markdown emphasis / code / headings / quotes.
  s = s.replace(/[*_`#>|]+/g, " ");
  // Horizontal rules and leading list bullets.
  s = s.replace(/^\s*[-–—*_]{3,}\s*$/gm, " ");
  s = s.replace(/^\s*[-*•]\s+/gm, " ");
  // Remove emoji & pictographic symbols (incl. variation selectors / ZWJ / flags).
  s = s.replace(
    /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{1F1E6}-\u{1F1FF}\u{FE00}-\u{FE0F}\u{200D}\u{20E3}\u{2190}-\u{21FF}\u{2300}-\u{23FF}\u{2900}-\u{297F}]/gu,
    "",
  );
  // Tidy whitespace + stray punctuation gaps.
  s = s.replace(/\s{2,}/g, " ").replace(/\s+([.,!?;:])/g, "$1").trim();
  return s;
}

export function useSpeechSynthesis(): SpeechState {
  const supported =
    typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
  const [speaking, setSpeaking] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  function pickVoice(bcpLang: string): SpeechSynthesisVoice | undefined {
    if (!supported) return undefined;
    const voices = window.speechSynthesis.getVoices();
    if (!voices || voices.length === 0) return undefined;
    const prefix = bcpLang.split("-")[0].toLowerCase();
    // Candidate voices for this language: exact BCP-47 first, then prefix.
    const exact = voices.filter(v => v.lang.toLowerCase() === bcpLang.toLowerCase());
    const byPrefix = voices.filter(v => v.lang.toLowerCase().startsWith(prefix));
    const pool = (exact.length ? exact : byPrefix);
    if (!pool.length) return undefined;
    // Rank: female name > neutral > male; exact-locale bonus.
    const ranked = [...pool].sort((a, b) => {
      const sa = _isFemaleName(a.name) + (a.lang.toLowerCase() === bcpLang.toLowerCase() ? 10 : 0);
      const sb = _isFemaleName(b.name) + (b.lang.toLowerCase() === bcpLang.toLowerCase() ? 10 : 0);
      return sb - sa;
    });
    // Never return a clearly-male voice if any non-male option exists.
    const nonMale = ranked.filter(v => _isFemaleName(v.name) >= 0);
    return (nonMale[0] || ranked[0]);
  }

  function speak(id: string, text: string, lang: string = "en") {
    if (!supported || !text?.trim()) return;
    const spoken = cleanForSpeech(text);
    if (!spoken) return;  // nothing left to say once emojis/markup stripped
    try {
      window.speechSynthesis.cancel();  // hard-stop any prior utterance
    } catch {}
    const utter = new SpeechSynthesisUtterance(spoken);
    const bcp = LANG_MAP[lang] || lang;
    utter.lang = bcp;
    const voice = pickVoice(bcp);
    if (voice) utter.voice = voice;
    // Warm, gentle "beautiful lady" delivery — slightly slower, softly higher.
    utter.rate = 0.97;
    utter.pitch = 1.12;
    utter.onend = () => {
      setSpeaking(false);
      setCurrentId(null);
    };
    utter.onerror = () => {
      setSpeaking(false);
      setCurrentId(null);
    };
    utteranceRef.current = utter;
    setCurrentId(id);
    setSpeaking(true);
    try {
      window.speechSynthesis.speak(utter);
    } catch {
      setSpeaking(false);
      setCurrentId(null);
    }
  }

  function stop() {
    if (!supported) return;
    try {
      window.speechSynthesis.cancel();
    } catch {}
    setSpeaking(false);
    setCurrentId(null);
  }

  // Cleanup on unmount so a widget closing doesn't leave a rogue voice speaking.
  useEffect(() => {
    return () => {
      if (supported) {
        try { window.speechSynthesis.cancel(); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Warm-up voice list on first mount — some browsers only populate the
  // voices array after the `voiceschanged` event fires.
  useEffect(() => {
    if (!supported) return;
    const bump = () => window.speechSynthesis.getVoices();
    bump();
    window.speechSynthesis.addEventListener?.("voiceschanged", bump);
    return () => window.speechSynthesis.removeEventListener?.("voiceschanged", bump);
  }, [supported]);

  return { supported, speaking, currentId, speak, stop };
}

export default useSpeechSynthesis;
